<template>
  <section id="experience-section" class="common-padding mb-20">
    <div class="flex flex-col">
      <h3
        id="experienceTitle"
        v-html="experienceTitle"
        class="heading-1 text-start leading-none font-bold uppercase"
      ></h3>
      <p
        class="heading-1 text-flax-smoke-400 text-opacity-50 hidden w-4/5 text-end font-extrabold sm:block"
      >
        ( {{ experienceData.length }} )
      </p>

      <div
        id="experience-text"
        class="md:column-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 text-center text-nowrap lg:col-start-2"
        >
          (
          <span class="inline sm:hidden">{{ experienceData.length }} </span>
          POSITIONS )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Experience in production AI/ML systems & infrastructure, software engineering, embodied AI, data engineering & analytics, and full-stack technologies.
        </p>
      </div>
    </div>

    <div class="mt-12 lg:mt-[10%]">
      <div
        v-for="(experience, index) in experienceData"
        :key="index"
        class="experience-card border-b border-flax-smoke-500/40 py-5 last:border-b-0"
      >
        <div class="flex flex-col sm:flex-row sm:items-center gap-y-0.5 gap-x-5">
          <span class="font-title! font-bold uppercase tracking-wide text-flax-smoke-100 text-lg sm:text-xl shrink-0">
            {{ experience.company }}
          </span>
          <span class="text-flax-smoke-300 text-base sm:text-lg flex-1 min-w-0">
            {{ experience.title }}
          </span>
          <span class="text-flax-smoke-500 text-base sm:text-lg font-mono whitespace-nowrap">
            {{ experience.period }}
          </span>
        </div>
        <p class="mt-2 text-flax-smoke-400/80 text-sm leading-relaxed">
          {{ experience.description }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { onBeforeMount, onMounted, ref } from 'vue';

  const experienceTitle = ref('Experience /');

  const experienceData = [
    {
      title: 'AI Developer Intern',
      company: 'CGI',
      location: 'Toronto, ON',
      period: 'Sep 2026 – Present',
      description: 'Build and deploy AI tooling for enterprise teams, including an agentic estimation tool.',
      achievements: [
        'Productionize an NLP privacy guardrail that detects personal information so enterprise teams can use LLMs on regulated data, deployed to Azure Kubernetes Service through GitOps.',
        'Automate reporting for an internal AI project quality-checker.',
      ],
    },
    {
      title: 'Software Engineer; Prev. Drone AI & Computer Vision Developer Intern',
      company: 'MiON Forest',
      location: 'Hamilton, ON (Remote)',
      period: 'Feb 2026 – Present (Part-time)',
      description: 'Build computer vision and autonomy pipeline for tree-planting drones: PyTorch instance segmentation, GeoTIFF georeferencing, a battery-constrained flight planner, and a YOLO11 terrain model trained on 4+ public datasets.',
      achievements: [
        'Implement a computer vision and geospatial pipeline using PyTorch for tree-planting drones, fusing instance segmentation with georeferencing on a 14k×10k GeoTIFF; a battery-constrained flight planner exports QGroundControl missions via nearest-neighbor routing.',
        'Train a YOLO11 terrain-segmentation model on a multi-source corpus curated from 4+ public datasets, unified into a 3-class drone-traversability schema (penetrable/impenetrable/unknown) via a reproducible MLOps preprocessing pipeline.',
      ],
    },
    {
      title: 'Research Assistant (Computer Vision & ML)',
      company: 'Elder Lab (Centre for Vision Research @ York University)',
      location: 'Toronto, ON',
      period: 'Sep 2026 – Present (Part-time)',
      description: 'Drone and fixed-camera perception for urban traffic analytics: camera calibration, ground-plane multi-object tracking, and detector benchmarking on aerial imagery.',
      achievements: [
        'Co-develop a drone and fixed-camera perception system for urban traffic analytics, calibrating per-camera homographies to project detections from 6 fixed cameras onto the ground plane for multi-object tracking of vehicles, cyclists, and pedestrians.',
        "Benchmark object detectors on 4K aerial imagery for accuracy and GPU latency to guide model selection for the lab's drone surveys.",
      ],
    },
        {
      title: 'Software Engineer Intern',
      company: 'Fullbay',
      location: 'Toronto, ON',
      period: 'Jul 2026 – Aug 2026',
      description: 'Instrumented AI observability infrastructure, curated golden datasets for model training and evaluation, and developed and maintained AI & full-stack features for Pitstop Classic.',
      achievements: [],
    },
    
    {
      title: 'AI & Software Engineer Intern',
      company: 'Pitstop (acquired)',
      location: 'Toronto, ON',
      period: 'Jan 2026 – Jun 2026',
      description: 'Architected a modular MCP chat agent orchestration system and an AI observability platform tracking accuracy, drift, and acceptance across 3 production models; developed and maintained full-stack features and data pipelines for a fleet management + predictive maintenance system.',
      achievements: [
        'Architected a modular skill-based orchestration system (POC) for an MCP chat agent with 6 specialized components, prompt routing, and centralized tool definitions using Node.js, PostgreSQL & OpenRouter API.',
        'Instrument an AI evaluation platform on AWS Lambda for 3 production models with deterministic flags + LLM-as-judge (Copilot), correction diffs (OCR), tier-match + Pinecone similarity (VMRS); surface acceptance, accuracy, and drift on a React dashboard.',
        'Develop, test, and maintain full-stack features and backend services for a production system, improving data consistency and fixing end-to-end bugs.',
        'Co-build and maintain data pipelines and AI evaluation workflows, processing inputs including sensor data via Geotab API for production algorithms, automating processes, and monitoring model performance.',
      ],
    },
    {
      title: 'Software Engineer',
      company: 'Develop for Good',
      location: 'San Francisco, CA (Remote)',
      period: 'Dec 2025 – Feb 2026 (Part-time)',
      description: 'Accessible digital platform with international payment gateway for Rise and Shine Dyslexic Organization in Uganda.',
      achievements: [
        'Developed an accessible digital solution for Rise and Shine Dyslexic Organization in Uganda with an integrated payment gateway for international and local donations.',
      ],
    },
    {
      title: 'AI & Software Developer Intern',
      company: 'PharmShift (York University BEST Startup)',
      location: 'Toronto, ON',
      period: 'Aug 2025 – Dec 2025 (Part-time)',
      description: 'RAG chatbot (LangChain + FAISS + DeepSeek) and real-time speech pipeline on GCP for an AI-powered OSCE pharmacy prep platform.',
      achievements: [
        'Architected a retrieval-augmented generation (RAG) chatbot using LangChain, DeepSeek API, and FAISS vector embeddings for contextual Q&A; integrate Gemini for automated scoring and personalized pharmacy consultation feedback.',
        'Developed an end-to-end speech processing pipeline using Google Cloud APIs and FastAPI WebSocket backend for an AI-powered OSCE prep platform; deployed AI services on Google Cloud Platform, cutting latency by 60% and enabling 50+ real-time patient sessions.',
      ],
    },
    {
      title: 'Data Developer Intern',
      company: 'Equitable Life Insurance Company of Canada',
      location: 'Waterloo, ON (Hybrid)',
      period: 'Sep 2025 – Dec 2025',
      description: 'Automated Python & VBA pipelines processing 5K+ records weekly, cutting manual workload 83%; Power BI dashboards + Azure Synapse migration exploratory work.',
      achievements: [
        'Built automated Python and VBA pipelines for data preprocessing, transformation, and validation of 5K+ records weekly, reducing manual workload by 83% and improving data governance and analytics readiness; generated insights using Power BI.',
        'Developed exploratory scripts to migrate and transform enterprise records from Genesys Cloud to Azure, implementing API integrations to streamline reporting processes.',
      ],
    },
    {
      title: 'Software Engineer Intern',
      company: 'Lost Link Network Solutions',
      location: 'Richmond Hill, ON',
      period: 'May 2025 – Aug 2025',
      description: 'React/FastAPI/Azure products for 5+ ops teams; 11 REST endpoints at 100 RPS, CI/CD pipeline, and LLM-driven workflow shipped to 9+ businesses saving $12K/yr each.',
      achievements: [
        'Delivered IT consulting solutions by building digital products with React, Node.js, and TypeScript; integrated Azure Monitor APIs & Azure AI Services to display real-time metrics for 12 assets per client, enhancing data visibility for 5+ operations teams.',
        'Engineered backend services and custom features with FastAPI and PostgreSQL, deploy 11 RESTful API endpoints to support 80+ users at 100 RPS; built a CI/CD pipeline with GitHub Actions to automate testing and deployment, reducing release time by 32%.',
        'Liaised with 8 internal stakeholders and client teams to translate business requirements into an AI-driven operational workflow leveraging LLMs and internal APIs; shipped with client-specific configurations to 9+ businesses, saving $12K annually per client.',
      ],
    },
  ];

  onBeforeMount(() => {
    experienceTitle.value = textSplitterIntoChar('Experience / ', true);
  });

  onMounted(() => {
    animateSplitText(
      '#experienceTitle .letters',
      '#experience-text',
      0.7,
      0.01,
      0,
    );
  });
</script>

<style scoped></style>
